import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  UserOutlined, 
  ShoppingCartOutlined, 
  DollarOutlined,
  GlobalOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { dashboardService } from '../../services/dashboardService';
import type { FC } from 'react';
import { TransactionsTable } from 'components/transactions';
import PaymentsTable from 'components/PaymentsTable/PaymentsTableDashboard(admin)';

interface DashboardStats {
  // Endpoints existants
  totalAgencies?: number;
  totalSubscribers?: number;
  totalRevenue?: number;
  activeEsims?: number;
  
  // Nouveaux endpoints de statistics-resource
  subscribersCount?: number;
  esimAvailable?: number;
  esimActif?: number;
  agencyUnusedBalance?: number;
  agencyTurnover?: number;
  agenciesCount?: number;
}

const AdminDashboard: FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Récupérer les statistiques de base existantes
      const [
        basicStatsResponse,
        subscribersResponse,
        esimAvailableResponse,
        esimActifResponse,
        unusedBalanceResponse,
        turnoverResponse,
        agenciesResponse
      ] = await Promise.allSettled([
        dashboardService.getStats(),
        dashboardService.getSubscribersCount(),
        dashboardService.getEsimAvailable(),
        dashboardService.getEsimActif(),
        dashboardService.getAgencyUnusedBalance(),
        dashboardService.getAgencyTurnover(null, null),
        dashboardService.getAgenciesCount()
      ]);

      const newStats: DashboardStats = {};

      // Traiter les statistiques de base
      if (basicStatsResponse.status === 'fulfilled') {
        Object.assign(newStats, basicStatsResponse.value.data);
      }

      // Traiter les nouvelles statistiques
      if (subscribersResponse.status === 'fulfilled') {
        newStats.subscribersCount = subscribersResponse.value.data;
      }

      if (esimAvailableResponse.status === 'fulfilled') {
        newStats.esimAvailable = esimAvailableResponse.value.data;
      }

      if (esimActifResponse.status === 'fulfilled') {
        newStats.esimActif = esimActifResponse.value.data;
      }

      if (unusedBalanceResponse.status === 'fulfilled') {
        newStats.agencyUnusedBalance = unusedBalanceResponse.value.data;
      }

      if (turnoverResponse.status === 'fulfilled') {
        newStats.agencyTurnover = turnoverResponse.value.data;
      }

      if (agenciesResponse.status === 'fulfilled') {
        newStats.agenciesCount = agenciesResponse.value.data;
      }

      setStats(newStats);
    } catch (error) {
      message.error(t('admin.dashboard.statsLoadError'));
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Métriques principales */}
      <Row gutter={[24, 24]}>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title={t('admin.dashboard.totalAgencies')}
              value={stats?.agenciesCount ?? stats?.totalAgencies ?? 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title={t('admin.dashboard.totalSubscribers')}
              value={stats?.subscribersCount ?? stats?.totalSubscribers ?? 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title={t('admin.dashboard.revenue')}
              value={stats?.agencyTurnover ?? stats?.totalRevenue ?? 0}
              prefix={<DollarOutlined />}
              suffix="€"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title={t('admin.dashboard.activeEsims')}
              value={stats?.esimActif ?? stats?.activeEsims ?? 0}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Nouvelles métriques des statistics-resource */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={12}>
          <Card loading={loading}>
            <Statistic
              title={t('admin.dashboard.availableEsims')}
              value={stats?.esimAvailable ?? 0}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card loading={loading}>
            <Statistic
              title={t('admin.dashboard.unusedBalance')}
              value={stats?.agencyUnusedBalance ?? 0}
              precision={2}
              prefix={<ClockCircleOutlined />}
              suffix="€"
            />
          </Card>
        </Col>
      </Row>

      {/* Transactions récentes */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title={t('admin.dashboard.recentTransactions')}>
            <PaymentsTable
              title=""
              //showFilters={true}
              pageSize={10}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;